import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase/firebase";

type Mode = "signin" | "signup";

export function SignIn() {
  const [mode, setMode] = useState<Mode>("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  function switchMode(next: Mode) {
    setMode(next);
    setError("");
    setPassword("");
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "signin") {
        await auth.signInWithEmailAndPassword(email, password);
      } else {
        const { user } = await auth.createUserWithEmailAndPassword(email, password);
        await user?.updateProfile({ displayName: name.trim() });
      }
      navigate("/");
    } catch (err: any) {
      const code: string = err?.code ?? "";
      if (code === "auth/user-not-found" || code === "auth/wrong-password") {
        setError("Invalid email or password.");
      } else if (code === "auth/email-already-in-use") {
        setError("An account with this email already exists.");
      } else if (code === "auth/weak-password") {
        setError("Password must be at least 6 characters.");
      } else if (code === "auth/too-many-requests") {
        setError("Too many attempts. Try again later.");
      } else {
        setError(err?.message ?? "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  const isSignIn = mode === "signin";

  return (
    <main className="stage">
      <div className="signin-sheet">
        <div className="mode-toggle" role="tablist">
          {(["signin", "signup"] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => switchMode(m)}
              className={mode === m ? "active" : ""}
              role="tab"
              aria-selected={mode === m}
            >
              {m === "signin" ? "sign in" : "create account"}
            </button>
          ))}
        </div>

        <h1>
          {isSignIn ? (
            <>
              welcome <em>back.</em>
            </>
          ) : (
            <>
              take a <em>name.</em>
            </>
          )}
        </h1>
        <p className="subtitle">
          {isSignIn
            ? "sign in to take the page"
            : "one name, one phrase at a time"}
        </p>

        <form onSubmit={handleSubmit}>
          {!isSignIn && (
            <div className="field">
              <label>name</label>
              <input
                type="text"
                placeholder="your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
              />
            </div>
          )}

          <div className="field">
            <label>email</label>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="field">
            <label>password</label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={isSignIn ? "current-password" : "new-password"}
            />
          </div>

          {error && <p className="error">{error}</p>}

          <button type="submit" disabled={loading} className="submit">
            {loading
              ? isSignIn
                ? "signing in…"
                : "creating account…"
              : isSignIn
              ? "sign in →"
              : "create account →"}
          </button>
        </form>
      </div>
    </main>
  );
}
